// Base API Response structure
export interface ApiResponse<T> {
  success: boolean
  result: T
  errorMessage?: string
}

// Paginated response structure
export interface PaginatedResponse<T> {
  items: T[]
  totalResults: number
  firstRow: number
  lastRow: number
  pages: number
  pageIndex: number
  itemsPerPage: number
}

// Image DTO from API
export interface ImageResDTO {
  id: number
  url: string
  order: number
}

// City DTO from API
export interface CityResDTO {
  id: number
  name: string
}

// Residence DTO from API
export interface ResidenceImageResDTO {
  id: number
  url: string
  order: number
}

// Residence DTO from API
export interface ResidenceResDTO {
  id: number
  name: string
  cityId: number
  cityName: string
  images: ResidenceImageResDTO[]
}

export interface ResidenceCreateReqDTO {
  name: string
  cityId: number
  images?: File[]
}

export interface ResidenceUpdateReqDTO {
  name: string
  cityId: number
  images?: File[]
  imagesIdToDelete?: number[]
}

export interface ResidenceFormData {
  name: string
  cityId: string
  images: File[]
  existingImages: ResidenceImage[]
  imagesToDelete: number[]
}

export interface ResidenceImage {
  id: number
  url: string
  order: number
}

export interface ResidenceFormErrors {
  name?: string
  cityId?: string
  images?: string
  general?: string
}

// Property Basic Update DTO
export interface PropertyBasicUpdateReqDTO {
  name: string
  price: number
  bathrooms: number
  roommates: number
  diningrooms: number
  isFeatured: boolean
}

// Email DTOs (NEW)
export interface ContactInfoEmailDTO {
  customerName: string
  customerEmail: string
  customerPhone: string
  message: string
}

export interface BookRequestEmailDTO {
  propid: number
  customerName: string
  customerEmail: string
  customerPhone: string
  message: string
  interestdatefrom: string
  interestdateto: string
}

// Amenity DTOs
export interface AmenityResDTO {
  id: number
  name: string
  isEnabled: boolean
  isDefault: boolean
  isActive: boolean
}

export interface AmenityCreateReqDTO {
  id: number
  name: string
  isDefault: boolean
  isActive: boolean
}

export interface AmenityUpdateReqDTO {
  name: string
  isDefault: boolean
  isActive: boolean
}

// Feature DTOs
export interface FeatureResDTO {
  id: number
  description: string
  isEnabled: boolean
  isDefault: boolean
  isActive: boolean
}

export interface FeatureCreateReqDTO {
  id: number
  description: string
  isDefault: boolean
  isActive: boolean
}

export interface FeatureUpdateReqDTO {
  description: string
  isDefault: boolean
  isActive: boolean
}

// Property List Item from API (based on your C# model)
export interface PropertyListItemResDTO {
  id: number
  name: string
  price: number
  bathrooms: number
  roommates: number
  diningrooms: number
  isFeatured: boolean
  images: string[]
  cityName: string
  residenceName: string
  coordinates: {
    latitude: number
    longitude: number
  }
}

// Property Detail Response from API
export interface PropertyDetailResDTO {
  id: number
  name: string
  description: string
  price: number
  bathrooms: number
  roommates: number
  diningrooms: number
  area: number
  isFeatured: boolean
  images: Array<{id: number, url: string}>
  residenceImages: Array<{id: number, url: string}>
  cityId: number
  cityDescription: string
  locationName: string
  address: string
  longitude: number
  latitude: number
  residenceId: number
  residenceDescription: string
  virtualTour?: string
  minimunMonthStayRequired: number
  includes: string[]
  rules: string[]
  faqs: Array<{
    question: string
    answer: string
  }>
  reviews: Array<{
    id: number
    userName: string
    rating: number
    comment: string
    date: string
  }>
  amenities: Array<{
    id: number
    name: string
    isEnabled: boolean
    isDefault: boolean
    status: number
  }>
  features?: Array<{
    id: number
    description: string
    isEnabled: boolean
    isDefault: boolean
    status: number
  }>
  rules?: Array<{
    id: number
    description: string
    isEnabled: boolean
    isDefault: boolean
    status: number
  }>
}

// Price Order Type enum (matching your .NET enum)
export enum PriceOrderType {
  None = 0,
  PriceAscending = 1,
  PriceDescending = 2,
}

// Transformed property for frontend use
export interface Property {
  id: number
  name: string
  price: number
  location: string
  neighborhood: string
  address: string
  bathrooms: number
  roommates: number
  diningRooms: number
  area?: number
  image: string
  images: string[]
  coordinates: [number, number]
  description: string
  longDescription?: string
  amenities?: string[]
  includes?: string[]
  rules?: string[]
  isFeatured?: boolean
  isAvailable?: boolean
  residenceId: number
}

// Property Detail for frontend use (CORRECTED)
export interface PropertyDetail {
  id: number
  name: string
  price: number
  location: string // cityDescription
  neighborhood: string // locationName
  address: string
  bathrooms: number
  roommates: number
  diningRooms: number
  coordinates: [number, number]
  description: string
  images: string[] // ONLY property images
  residenceImages: string[] // ONLY residence images
  virtualTour?: string
  minimumStay: number
  isFeatured: boolean
  residenceId: number
  residenceDescription: string
  propertyImageIds?: number[] // IDs of property images for deletion tracking
  amenities?: Array<{
    id: number
    name: string
    isEnabled: boolean
    isDefault: boolean
    status: number
  }>
  features?: Array<{
    id: number
    description: string
    isEnabled: boolean
    isDefault: boolean
    status: number
  }>
  rules?: Array<{
    id: number
    description: string
    isEnabled: boolean
    isDefault: boolean
    status: number
  }>
}

// City for frontend use
export interface City {
  id: number
  name: string
}

// Residence for frontend use
export interface Residence {
  id: number
  name: string
  cityId: number
  cityName: string
  images: string[]
  imageIds?: number[]
}

// Amenity for frontend use
export interface Amenity {
  id: number
  name: string
  isEnabled: boolean
  isDefault: boolean
  isActive: boolean
}

// Feature for frontend use
export interface Feature {
  id: number
  description: string
  isEnabled: boolean
  isDefault: boolean
  isActive: boolean
}

// Rule for frontend use
export interface Rule {
  id: number
  description: string
  isEnabled: boolean
  isDefault: boolean
  isActive: boolean
}

// API Query parameters (updated to match your API)
export interface PropertyQueryParams {
  pageIndex?: number
  pageSize?: number
  cityId?: number
  minPrice?: number
  maxPrice?: number
  minRoommates?: number
  maxRoommates?: number
  minBathrooms?: number
  maxBathrooms?: number
  isFeatured?: boolean
  searchTerm?: string
}

// Residence Query parameters
export interface ResidenceQueryParams {
  pageIndex?: number
  pageSize?: number
  cityId?: number
  searchvalue?: string
}

// Amenity Query parameters
export interface AmenityQueryParams {
  pageIndex?: number
  pageSize?: number
  searchvalue?: string
}

// Feature Query parameters
export interface FeatureQueryParams {
  pageIndex?: number
  pageSize?: number
  searchvalue?: string
}

// Rule DTOs
export interface RuleResDTO {
  id: number
  description: string
  isEnabled: boolean
  isDefault: boolean
  isActive: boolean
}

export interface RuleCreateReqDTO {
  id: number
  description: string
  isDefault: boolean
  isActive: boolean
}

export interface RuleUpdateReqDTO {
  description: string
  isDefault: boolean
  isActive: boolean
}

// Rule Query parameters
export interface RuleQueryParams {
  pageIndex?: number
  pageSize?: number
  searchvalue?: string
}

// FAQ DTOs
export interface FaqResDTO {
  id: number
  question: string
  answer: string
}

export interface FaqCreateReqDTO {
  question: string
  answer: string
}

export interface FaqUpdateReqDTO {
  question: string
  answer: string
}

// FAQ Query parameters
export interface FaqQueryParams {
  pageIndex?: number
  pageSize?: number
  searchvalue?: string
}

// FAQ for frontend use
export interface Faq {
  id: number
  question: string
  answer: string
}

// Contact Info DTOs
export interface ContactInfoResDTO {
  displayPhone: string
  displayEmail: string
}

export interface ContactInfoUpdateReqDTO {
  displayPhone: string
  displayEmail: string
}

// Filter state for frontend
export interface FilterState {
  cityId: number | null
  priceSort: PriceOrderType
  searchText: string
}

// API Error type
export interface ApiError {
  message: string
  status?: number
  errors?: string[]
}
